<template>
  <transition name="fade">
    <div v-if="visible" class="home-page">
      <div
        class="background-layer"
        :style="{
          backgroundImage: posterUrl ? `url(${posterUrl})` : 'none',
          backgroundSize: posterFit,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }"
      />

      <!-- The shade is the title's and the duration's, which read on any poster through it; with
           them hidden it only darkens the poster. The badge and the play button carry their own
           background. -->
      <div v-if="showInfo && title" class="overlay-gradient"></div>

      <button
        class="home-page__play-trigger"
        :aria-label="t('player.controls.play')"
        @click="emit('play')"
      >
        <div class="content-layer" aria-hidden="true">
          <!-- Playlist badge -->
          <div
            v-if="isPlaylist && totalItems > 1"
            class="playlist-badge"
          >
            <span class="playlist-icon" v-html="playlistIcon"></span>
            <span class="video-count">{{ t('player.homepage.playlistBadge', { count: totalItems }) }}</span>
          </div>

          <!-- Play icon (absolute center) -->
          <div v-if="showPlayButton" class="play-button-wrapper">
            <div class="play-button-visual">
              <div class="play-icon" v-html="playIcon"></div>
            </div>
          </div>

          <!-- Video info (absolute bottom) -->
          <div v-if="showInfo && title" class="video-info">
            <!-- Not a heading: the player sits inside a page that has its own `h1` (a channel's
                 item page), and a heading is not content a `<button>` may hold. -->
            <span class="video-title">{{ title }}</span>
            <div v-if="duration" class="video-metadata">
              <span class="duration">{{ formatDuration(duration) }}</span>
            </div>
          </div>
        </div>
      </button>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { playIcon, playlistIcon } from '../../assets/icons'
import { useVideoI18n } from '../../i18n'

interface Props {
  visible: boolean
  posterUrl?: string
  posterFit?: 'contain' | 'cover'
  title?: string
  duration?: number
  showInfo?: boolean
  showPlayButton?: boolean
  isPlaylist?: boolean
  totalItems?: number
}

withDefaults(defineProps<Props>(), {
  visible: true,
  posterFit: 'contain',
  showInfo: true,
  showPlayButton: true,
  isPlaylist: false,
  totalItems: 0,
})

const emit = defineEmits<{
  play: []
}>()

const { t } = useVideoI18n()

function formatDuration(seconds: number): string {
  if (!seconds) return ''
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }
  return `${m}:${s.toString().padStart(2, '0')}`
}
</script>

<style scoped lang="scss">
@use '../../styles/mixins' as *;

.home-page {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
  overflow: hidden;
}

.background-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.overlay-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(0, 0, 0, 0.05) 40%,
    rgba(0, 0, 0, 0.4) 75%,
    rgba(0, 0, 0, 0.75) 100%
  );
}

.home-page__play-trigger {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: inherit;
  font: inherit;
  text-align: inherit;

  &:focus-visible {
    outline: 3px solid var(--vp-primary-color, #007bff);
    outline-offset: -3px;
  }
}

.content-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.play-button-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.play-button-visual {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background-color: var(--vp-primary-color, #007bff);
  border: 3px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease, opacity 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  opacity: 0.9;

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
  }
}

.home-page__play-trigger:hover .play-button-visual {
  transform: scale(1.1);
  box-shadow: 0 6px 30px rgba(0, 0, 0, 0.4);
  opacity: 1;
}

.home-page__play-trigger:active .play-button-visual {
  transform: scale(0.95);
}

.play-icon {
  width: 32px;
  height: 32px;
  color: white;
  margin-left: 4px;

  :deep(svg) {
    width: 100%;
    height: 100%;
  }

  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
  }
}

.playlist-badge {
  position: absolute;
  top: 0;
  left: 0;
  margin: 20px;
  background: var(--vp-primary-color, #007bff);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 24px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  z-index: 10;
  white-space: nowrap;

  // On a narrow frame the badge reached down to the play button.
  @include player-compact {
    margin: 10px;
    padding: 5px 10px;
    gap: 6px;
    border-width: 1px;

    .playlist-icon {
      width: 16px;
      height: 16px;
    }

    .video-count {
      font-size: 0.8125rem;
    }
  }
}

.playlist-icon {
  display: flex;
  width: 18px;
  height: 18px;
  color: white;

  :deep(svg) {
    width: 100%;
    height: 100%;
  }
}

.video-count {
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1;
}

.video-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 20px;
  color: white;
  user-select: none;
  pointer-events: none;

  @media (max-width: 768px) {
    padding: 12px 16px;
  }
}

.video-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
}

.video-metadata {
  margin-top: 4px;
  font-size: 0.8rem;
  opacity: 0.9;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .play-button-visual {
    transition: none;
  }

  .home-page__play-trigger:hover .play-button-visual {
    transform: none;
  }

  .home-page__play-trigger:active .play-button-visual {
    transform: none;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition-duration: 1ms;
  }
}
</style>
